import './update-banner.scss';

import React, { useEffect, useState } from 'react';

import useVersionCheck from '@/hooks/useVersionCheck';
import RefreshIcon from '@/media/icons/refresh.svg?react';
import { handleError } from '@/services/handleError';
import { isMacDevice } from '@/services/helpers/device';

const clearCacheStorage = async (): Promise<void> => {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
};

const unregisterServiceWorkers = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((reg) => reg.unregister()));
};

const KeysCombination = ({ keys }: { keys: string[] }) => {
  return (
    <span className="update-banner__keys-combination">
      {keys.map((key, index) => (
        <React.Fragment key={key}>
          <span className="update-banner__key">{key}</span>
          {index < keys.length - 1 && (
            <span className="update-banner__key-separator">+</span>
          )}
        </React.Fragment>
      ))}
    </span>
  );
};

function UpdateBanner() {
  const { updateAvailable, handleNewVersionUpdate } = useVersionCheck();
  const [isSkipped, setIsSkipped] = useState(false);
  const isMac = isMacDevice();

  const handleSkip = () => {
    setIsSkipped(true);
  };

  const handleUpdate = async (): Promise<void> => {
    handleNewVersionUpdate();

    try {
      await Promise.all([
        fetch(window.location.origin, { cache: 'reload' }),
        clearCacheStorage(),
        unregisterServiceWorkers()
      ]);
    } catch (error) {
      handleError(error, { notify: false });
    }

    window.location.reload();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'r'
      ) {
        handleNewVersionUpdate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNewVersionUpdate]);

  if (!updateAvailable || isSkipped) return null;
  return (
    <div className="update-banner">
      <div className="update-banner__inner">
        <div className="update-banner__content">
          <RefreshIcon className="update-banner__icon" />
          <span className="update-banner__text">
            Доступна новая версия сайта
          </span>
        </div>
        <div className="update-banner__actions">
          <button className="update-banner__action" onClick={handleSkip}>
            Позже
          </button>
          <button
            className="update-banner__action update-banner__action--update"
            onClick={handleUpdate}
          >
            Обновить
          </button>
        </div>
        <span className="update-banner__hint">
          Или нажмите{' '}
          <KeysCombination keys={[isMac ? 'Cmd' : 'Ctrl', 'Shift', 'R']} /> для
          обновления
        </span>
      </div>
    </div>
  );
}

export default UpdateBanner;
